import { useCallback } from 'react';

/** Material UI Packages. */
import { GridRowEditStopReasons, GridRowModes } from '@mui/x-data-grid-premium';

/** Redux Packages. */
import { useDispatch, useSelector } from 'react-redux';

/** Redux Reducers. */
import { updateRoute, addRoute, deleteRoute } from 'shared/store/slices/routeSlice';
import { setSnackbar } from 'shared/store/slices/snackBarSlice';

/** Internal Components. */
import { updateSocketData, createSocketData } from 'shared/lib/fetchSocket';

import {
    airColGroupingModel,
    oceanColGroupingModel,
    railColGroupingModel,
} from '../../../lib/RouteDef';

export default function RouteDataGridHandlers(props) {
    /** Get props from TransportRoute.js. */
    const { tabValue, rowModesModel, setRowModesModel, savePrevRoute, restoreRouteRef } = props;

    /** Load dispatch to update routes. */
    const dispatch = useDispatch();

    /** Route page info. */
    const routeData = useSelector((state) => state.routes);

    /** Handler that triggered when a row enters editr mode via a double-click event.
     *  Save the init value in state in order to cancel modification.
     */
    const onRowEditStart = useCallback(
        (params, event) => {
            savePrevRoute(params.id);
        },
        [routeData]
    );

    /** Handler to prevent mui's default behavior when switching row's mode from edit to view. */
    const handleRowEditStop = useCallback((params, event) => {
        if (params.reason === GridRowEditStopReasons.rowFocusOut) {
            event.defaultMuiPrevented = true;
        }
    }, []);

    /** Function to prevent the datagrid's default enter key behavior when the autocomplete popper is visible. */
    const handleCellKeyDown = useCallback((params, event) => {
        if (event.key === 'Enter') {
            const autocompleteDom = document.querySelector('.MuiAutocomplete-popper');
            const autocompleteOpen = autocompleteDom !== null;
            if (autocompleteOpen) {
                event.preventDefault();
                event.stopPropagation();
            }
        }
    }, []);

    /** Mapper for column grouping model names. */
    const groupingModel = useCallback((tabValue) => {
        const groupingModel = {
            1: oceanColGroupingModel,
            2: airColGroupingModel,
            3: railColGroupingModel,
        };
        return groupingModel[tabValue];
    }, []);

    /** Handler for row updates after editing in datagrid. */
    const processRowUpdate = (newRow, oldRow) => {
        if (!oldRow) {
            console.error('Old row not found for ID:', newRow.id);
            throw new Error('Row not found');
        }

        const liveRow = (routeData.il_route || []).find((r) => r.id === newRow.id) || {};
        const effectiveRow = {
            ...oldRow,
            ...newRow,
            pol_code: liveRow.pol_code ?? newRow.pol_code,
            pol_name: liveRow.pol_name ?? newRow.pol_name,
            pol_country: liveRow.pol_country ?? newRow.pol_country,
            pod_code: liveRow.pod_code ?? newRow.pod_code,
            pod_name: liveRow.pod_name ?? newRow.pod_name,
            pod_country: liveRow.pod_country ?? newRow.pod_country,
        };

        // Case 1: No changes → return as-is
        const baseRow = restoreRouteRef?.current?.[newRow.id] ?? oldRow;
        const changes = Object.keys(effectiveRow).reduce((acc, key) => {
            const newValue = effectiveRow[key];
            const oldValue = baseRow[key];

            // null/undefined handling improvement
            if (newValue !== oldValue) {
                // If the value is an object or array, compare them using JSON.stringify.
                if (typeof newValue === 'object' && typeof oldValue === 'object') {
                    if (JSON.stringify(newValue) !== JSON.stringify(oldValue)) {
                        acc[key] = newValue;
                    }
                } else {
                    acc[key] = newValue;
                }
            }
            return acc;
        }, {});

        // 신규 행은 변경점 비교로 걸러내면 안 된다. 거점을 고르면 handleAutocompleteChange가
        // api.updateRows로 행 모델을 먼저 갱신하는데, 신규 행은 편집을 코드로 시작해
        // onRowEditStart가 뜨지 않아 비교 기준(restoreRouteRef)이 비어 있다. 그래서
        // baseRow가 이미 갱신된 oldRow가 되고, Lead Time을 기본값 1에서 건드리지 않으면
        // 달라진 칸이 하나도 없다고 판단해 저장이 조용히 무시된다.
        const isNewRoute = oldRow.isNew === true;

        if (!isNewRoute && Object.keys(changes).length === 0) {
            return newRow;
        }

        // Create updatedRow with all changes.
        const updatedRow = { ...effectiveRow, isNew: false };

        /**
         * Validate row values before saving.
         * Returns an error message string if invalid, or null if valid.
         */
        const validateRow = (row) => {
            if (!row.pol_name || !row.pod_name) {
                return 'Please input location.';
            }
            if (!row.leadtime || Number(row.leadtime) <= 0 || !Number.isInteger(Number(row.leadtime))) {
                return 'Please check your days. Lead time must be a positive integer.';
            }
            if (row.pol_name === row.pod_name) {
                if (tabValue === 3) {
                    return 'POD and F.DEST cannot be same.';
                }
                return tabValue === 1 ? 'POL and POD cannot be same.' : 'AOL and AOD cannot be same.';
            }
            return null;
        };

        // Case 2: Validation failed → show warning and throw error
        const validationError = validateRow(updatedRow);

        if (validationError !== null) {
            dispatch(
                setSnackbar({
                    open: true,
                    message: validationError,
                    severity: 'warning',
                })
            );
            throw new Error('Invalid value!');
        }

        // Case 3: Check if this is a new route or update
        if (isNewRoute) {
            // 새로 추가하는 경우
            createSocketData(
                dispatch,
                tabValue,
                'row',
                'il_route',
                null,
                updatedRow,
                (receivedData) => {
                    dispatch(deleteRoute(updatedRow.id));
                    dispatch(addRoute(receivedData.data));
                    dispatch(
                        setSnackbar({
                            open: true,
                            message: receivedData.message || 'Successfully created the new route.',
                            severity: 'success',
                        })
                    );
                },
                (response) => {
                    setRowModesModel({ ...rowModesModel, [updatedRow.id]: { mode: GridRowModes.Edit } });
                }
            );
        } else {
            // 업데이트하는 경우
            updateSocketData(
                dispatch,
                tabValue,
                'row',
                'il_route',
                null,
                updatedRow,
                (receivedData) => {
                    dispatch(updateRoute({ newRow: oldRow, updatedRow: receivedData.data }));
                    dispatch(
                        setSnackbar({
                            open: true,
                            message: receivedData.message,
                            severity: 'success',
                        })
                    );
                },
                () => {
                    setRowModesModel({ ...rowModesModel, [updatedRow.id]: { mode: GridRowModes.Edit } });
                }
            );
        }

        return updatedRow;
    };

    return { processRowUpdate, onRowEditStart, handleRowEditStop, handleCellKeyDown, groupingModel };
}
